import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseRounded as CloseRoundedIcon,
  LocalOffer as LocalOfferIcon,
  OpenInNew as OpenInNewIcon,
  SwapHoriz as SwapHorizIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../hostApiClient';
import { apiClient } from '../publicApiClient';
import { OrganizationType } from '../models/Organization';
import {
  isRehearsalStudioPlanTransition,
  requiresOrganizationTypeChangeConfirm,
  SubscriptionPlanCode,
  SubscriptionPlanSummary,
  SubscriptionStatus,
} from '../models/Subscription';
import { redirectToCheckout } from '../services/stripeService';
import {
  clearOnboardingDisplayName,
  ensureProviderProfileForPlan,
} from '../utils/subscriptionOnboarding';
import {
  clearSubscriptionAwaitingStripe,
  markSubscriptionAwaitingStripe,
} from '../utils/subscriptionStripeReturn';
import { authApiClient } from '../authApiClient';
import { authService } from '../services/AuthService';
import SubscriptionPlanCard from './subscription/SubscriptionPlanCard';
import {
  isPopularPlanCode,
  isPrivateOfferPlanCode,
  planCodeToTier,
  resolvePlanAccent,
  resolveSpecialOfferAccent,
  tierRank,
} from './subscription/subscriptionPlanUi';

interface SubscriptionPlansProps {
  organizationType?: OrganizationType | null;
  currentPlanCode?: SubscriptionPlanCode | null;
  currentStatus?: SubscriptionStatus | null;
  /**
   * `select` — no open subscription; checkout a price.
   * `change` — open subscription; private offers use `/change`, public plans use portal.
   * `preview` — catalogue browse only; no Stripe. Parent handles CTA via `onPlanInterest`.
   */
  mode?: 'select' | 'change' | 'preview';
  /**
   * Force `GET /v1/host/subscription/plans` (includes private offers).
   * Default: host catalogue except onboarding / anonymous preview.
   */
  useHostCatalogue?: boolean;
  /** Signup / first-plan flow: softer copy and trial-oriented CTA. */
  onboarding?: boolean;
  /** Parent supplies the page title (pricing / marketing). */
  hideHeader?: boolean;
  /** Called in `preview` mode when a plan card is activated. */
  onPlanInterest?: (plan: SubscriptionPlanSummary) => void;
  onSubscriptionChange?: () => void;
}

export interface SubscriptionPlansHandle {
  /** Start checkout for the highlighted “Most Popular” plan in the active org-type tab. */
  selectPopularPlan: () => void;
}

const ORG_TYPE_ORDER: OrganizationType[] = [
  OrganizationType.INDIVIDUAL_TUTOR,
  OrganizationType.MUSIC_SCHOOL,
  OrganizationType.REHEARSAL_STUDIO,
  OrganizationType.BUSINESS,
];

const SPECIAL_OFFERS_SEGMENT = 'SPECIAL_OFFERS' as const;
type CatalogueSegment = typeof SPECIAL_OFFERS_SEGMENT | OrganizationType;

/**
 * Reusable catalogue picker for onboarding, public pricing, reactivation, and plan changes.
 * Pair with {@link CurrentSubscriptionCard} on the host billing page when a plan is active.
 */
const SubscriptionPlans = forwardRef<SubscriptionPlansHandle, SubscriptionPlansProps>(
  function SubscriptionPlans(
    {
      organizationType,
      currentPlanCode,
      currentStatus,
      mode = 'select',
      useHostCatalogue,
      onboarding = false,
      hideHeader = false,
      onPlanInterest,
      onSubscriptionChange,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const theme = useTheme();
    const usePublicCatalogue =
      useHostCatalogue === true
        ? false
        : useHostCatalogue === false
          ? true
          : onboarding || mode === 'preview';
    /** Trial marketing only when browsing without an existing plan code. */
    const showTrialPricing =
      onboarding || (mode === 'preview' && !currentPlanCode);
    const subscribedPreview = mode === 'preview' && Boolean(currentPlanCode);

    const [plans, setPlans] = useState<SubscriptionPlanSummary[]>([]);
    const [selectedSegment, setSelectedSegment] = useState<CatalogueSegment | null>(null);
    const [actionPriceId, setActionPriceId] = useState<string | null>(null);
    const [pendingTypeChangePlan, setPendingTypeChangePlan] =
      useState<SubscriptionPlanSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const organizationTypeRef = useRef(organizationType);
    organizationTypeRef.current = organizationType;
    const currentPlanCodeRef = useRef(currentPlanCode);
    currentPlanCodeRef.current = currentPlanCode;
    const userPickedSegmentRef = useRef(false);

    const orgTypeLabel = (type: OrganizationType) =>
      t(`subscription.orgTypes.${type}`, type.replace(/_/g, ' '));

    const specialOfferPlans = useMemo(
      () => plans.filter((plan) => isPrivateOfferPlanCode(plan.code)),
      [plans],
    );
    const hasSpecialOffers = specialOfferPlans.length > 0;

    const availableOrgTypes = useMemo(() => {
      const present = new Set(
        plans
          .filter((plan) => !isPrivateOfferPlanCode(plan.code))
          .map((plan) => plan.organizationType),
      );
      return ORG_TYPE_ORDER.filter((type) => present.has(type));
    }, [plans]);

    const catalogueSegments = useMemo((): CatalogueSegment[] => {
      return hasSpecialOffers
        ? [SPECIAL_OFFERS_SEGMENT, ...availableOrgTypes]
        : availableOrgTypes;
    }, [availableOrgTypes, hasSpecialOffers]);

    const resolveDefaultSegment = (
      segments: CatalogueSegment[],
      current: CatalogueSegment | null,
    ): CatalogueSegment | null => {
      if (current && segments.includes(current)) {
        return current;
      }
      // Prefer Special offers when present; otherwise the org's current type tab.
      if (segments.includes(SPECIAL_OFFERS_SEGMENT)) {
        return SPECIAL_OFFERS_SEGMENT;
      }
      const preferred = organizationTypeRef.current;
      if (preferred && segments.includes(preferred)) {
        return preferred;
      }
      return segments[0] ?? null;
    };

    const fetchSubscriptionPlans = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const subscriptionPlans = usePublicCatalogue
          ? await apiClient.getSubscriptionPlans()
          : await hostApiClient.getSubscriptionPlans();
        const sorted = [...subscriptionPlans].sort((a, b) => {
          if (a.organizationType !== b.organizationType) {
            return a.organizationType.localeCompare(b.organizationType);
          }
          const aOffer = isPrivateOfferPlanCode(a.code) ? 0 : 1;
          const bOffer = isPrivateOfferPlanCode(b.code) ? 0 : 1;
          if (aOffer !== bOffer) {
            return aOffer - bOffer;
          }
          return tierRank[a.tier] - tierRank[b.tier];
        });
        const present = new Set(
          sorted
            .filter((plan) => !isPrivateOfferPlanCode(plan.code))
            .map((plan) => plan.organizationType),
        );
        const types = ORG_TYPE_ORDER.filter((type) => present.has(type));
        const hasOffers = sorted.some((plan) => isPrivateOfferPlanCode(plan.code));
        const segments: CatalogueSegment[] = hasOffers
          ? [SPECIAL_OFFERS_SEGMENT, ...types]
          : types;
        setPlans(sorted);
        setSelectedSegment((current) => resolveDefaultSegment(segments, current));
      } catch (err) {
        setError(t('subscription.fetchPlansError', 'Failed to fetch subscription plans'));
        console.error('Error fetching subscription plans:', err);
      } finally {
        setLoading(false);
      }
    }, [usePublicCatalogue, t]);

    useEffect(() => {
      void fetchSubscriptionPlans();
    }, [fetchSubscriptionPlans]);

    useEffect(() => {
      if (catalogueSegments.length === 0) {
        setSelectedSegment(null);
        return;
      }
      setSelectedSegment((current) => {
        if (userPickedSegmentRef.current && current && catalogueSegments.includes(current)) {
          return current;
        }
        return resolveDefaultSegment(catalogueSegments, null);
      });
    }, [catalogueSegments, organizationType, currentPlanCode]);

    const visiblePlans = useMemo(() => {
      if (!selectedSegment) return [];
      if (selectedSegment === SPECIAL_OFFERS_SEGMENT) {
        return specialOfferPlans;
      }
      return plans.filter(
        (plan) =>
          plan.organizationType === selectedSegment && !isPrivateOfferPlanCode(plan.code),
      );
    }, [plans, selectedSegment, specialOfferPlans]);

    const handleSegmentChange = (
      _event: React.MouseEvent<HTMLElement>,
      next: CatalogueSegment | null,
    ) => {
      if (next != null) {
        userPickedSegmentRef.current = true;
        setSelectedSegment(next);
      }
    };

    const runCheckout = async (plan: SubscriptionPlanSummary) => {
      try {
        setActionPriceId(plan.priceId);
        setError(null);

        if (onboarding) {
          const ensured = await ensureProviderProfileForPlan({
            plan,
            completeHostProfile: (request) => authApiClient.completeHostProfile(request),
            completeTutorProfile: (request) => authApiClient.completeTutorProfile(request),
            refreshSession: () => authService.refreshSessionAfterProfileCompletion(),
            getRoles: () => authService.getRoles(),
          });
          if (!ensured.ok) {
            setError(
              ensured.reason === 'missing_session'
                ? t(
                    'subscription.onboarding.tutorProfileMissing',
                    'Could not prepare your organization. Please sign in again and retry.',
                  )
                : t(
                    'subscription.onboarding.tutorProfileFailed',
                    'Could not enable provider access for this plan. Please try again.',
                  ),
            );
            return;
          }
        }

        markSubscriptionAwaitingStripe({
          planCode: currentPlanCode ?? null,
          status: currentStatus ?? null,
          organizationType: organizationType ?? plan.organizationType ?? null,
        });
        const response = await hostApiClient.checkoutSubscription({
          subscriptionPlanPriceId: plan.priceId,
        });

        if (response.stripeCheckoutSessionId) {
          clearOnboardingDisplayName();
          await redirectToCheckout(response.stripeCheckoutSessionId);
        }

        onSubscriptionChange?.();
      } catch (err) {
        setError(t('subscription.checkoutError', 'Failed to create checkout session'));
        console.error('Checkout error:', err);
      } finally {
        setActionPriceId(null);
      }
    };

    const runPortalSwitch = async (plan: SubscriptionPlanSummary) => {
      try {
        setActionPriceId(plan.priceId);
        setError(null);
        markSubscriptionAwaitingStripe({
          planCode: currentPlanCode ?? null,
          status: currentStatus ?? null,
          organizationType: organizationType ?? null,
        });
        const { url } = await hostApiClient.createSubscriptionPortal({
          subscriptionPlanPriceId: plan.priceId,
        });
        if (url) {
          window.location.assign(url);
        }
      } catch (err) {
        setError(t('subscription.portalError', 'Failed to open billing portal'));
        console.error('Portal plan switch error:', err);
        void fetchSubscriptionPlans();
      } finally {
        setActionPriceId(null);
      }
    };

    /** Private offered price while subscribed — backend updates Stripe; no portal redirect. */
    const runPrivateOfferChange = async (plan: SubscriptionPlanSummary) => {
      try {
        setActionPriceId(plan.priceId);
        setError(null);
        markSubscriptionAwaitingStripe({
          planCode: currentPlanCode ?? null,
          status: currentStatus ?? null,
          organizationType: organizationType ?? plan.organizationType ?? null,
        });
        await hostApiClient.changeSubscriptionPlan({
          subscriptionPlanPriceId: plan.priceId,
        });
        onSubscriptionChange?.();
      } catch (err) {
        clearSubscriptionAwaitingStripe();
        setError(
          t('subscription.changePlanError', 'Failed to change subscription plan. Please try again.'),
        );
        console.error('Private offer plan change error:', err);
        void fetchSubscriptionPlans();
      } finally {
        setActionPriceId(null);
      }
    };

    const executePlanAction = async (plan: SubscriptionPlanSummary) => {
      if (plan.code === currentPlanCode) return;
      if (mode === 'preview') {
        onPlanInterest?.(plan);
        return;
      }
      if (mode === 'change') {
        if (isPrivateOfferPlanCode(plan.code)) {
          await runPrivateOfferChange(plan);
        } else {
          await runPortalSwitch(plan);
        }
        return;
      }
      await runCheckout(plan);
    };

    const requestPlanAction = (plan: SubscriptionPlanSummary) => {
      if (plan.code === currentPlanCode || Boolean(actionPriceId)) return;
      if (mode === 'preview') {
        onPlanInterest?.(plan);
        return;
      }
      if (requiresOrganizationTypeChangeConfirm(organizationType, plan.organizationType)) {
        setPendingTypeChangePlan(plan);
        return;
      }
      void executePlanAction(plan);
    };
    const requestPlanActionRef = useRef(requestPlanAction);
    requestPlanActionRef.current = requestPlanAction;

    useImperativeHandle(
      ref,
      () => ({
        selectPopularPlan: () => {
          const popular =
            visiblePlans.find((plan) => isPopularPlanCode(plan.code)) ??
            visiblePlans[visiblePlans.length - 1];
          if (popular) {
            requestPlanActionRef.current(popular);
          }
        },
      }),
      [visiblePlans],
    );

    const confirmTypeChange = () => {
      const plan = pendingTypeChangePlan;
      setPendingTypeChangePlan(null);
      if (plan) {
        void executePlanAction(plan);
      }
    };

    const pendingStudioTransition =
      pendingTypeChangePlan != null &&
      isRehearsalStudioPlanTransition(organizationType, pendingTypeChangePlan.organizationType);

    const actionLabel = (plan: SubscriptionPlanSummary) => {
      if (plan.code === currentPlanCode) {
        return t('subscription.currentPlan', 'Current');
      }
      if (mode === 'change') {
        return t('subscription.switchPlan', 'Switch');
      }
      if (subscribedPreview) {
        return isPrivateOfferPlanCode(plan.code)
          ? t('subscription.switchPlan', 'Switch')
          : t('subscription.managePlan', 'Manage plan');
      }
      if (onboarding || mode === 'preview') {
        return t('subscription.onboarding.startPlan', 'Start Free Trial');
      }
      // No open subscription (select / reactivation): same CTA on every plan card.
      // Org-type change is still confirmed in the dialog after click.
      return t('subscription.subscribe', 'Subscribe');
    };

    if (loading || (plans.length > 0 && !selectedSegment)) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress />
        </Box>
      );
    }

    if (error && plans.length === 0) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (plans.length === 0) {
      return (
        <Alert severity="info">
          {t('subscription.noPlansAvailable', 'No subscription plans available')}
        </Alert>
      );
    }

    return (
      <Box sx={{ py: onboarding || hideHeader ? 0 : 2 }}>
        {!onboarding && !hideHeader && (
          <>
            <Typography variant="h5" gutterBottom>
              {mode === 'change'
                ? t('subscription.plansHeading', 'Plans')
                : t('subscription.title', 'Choose Your Plan')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {mode === 'change'
                ? t(
                    'subscription.plansPortalHint',
                    'Compare prices and commissions across organization types. Public plans confirm in Stripe; special offers apply directly. Your organization type updates after activation.',
                  )
                : t('subscription.subtitle', 'Select a monthly plan for your organization')}
            </Typography>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {catalogueSegments.length > 1 && selectedSegment && (
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <ToggleButtonGroup
              exclusive
              value={selectedSegment}
              onChange={handleSegmentChange}
              aria-label={t('subscription.catalogueSegmentsLabel', 'Plan catalogue')}
              sx={{
                flexWrap: 'wrap',
                justifyContent: 'center',
                '& .MuiToggleButton-root': {
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                },
              }}
            >
              {catalogueSegments.map((segment) => {
                const isOffers = segment === SPECIAL_OFFERS_SEGMENT;
                const onPrivateOffer = isPrivateOfferPlanCode(currentPlanCode);
                // Only mark Current when a plan is actually subscribed; org type alone is not enough.
                // Private offers live under Special offers only — do not also mark the org-type tab.
                const showCurrentChip =
                  Boolean(currentPlanCode) &&
                  (isOffers
                    ? onPrivateOffer
                    : !onPrivateOffer && organizationType === segment);
                return (
                  <ToggleButton key={segment} value={segment}>
                    {isOffers
                      ? t('subscription.specialOffers', 'Special offers')
                      : orgTypeLabel(segment)}
                    {isOffers && (
                      <LocalOfferIcon
                        sx={{ ml: 1, fontSize: 16, color: 'warning.main' }}
                        aria-hidden
                      />
                    )}
                    {showCurrentChip && (
                      <Chip
                        label={t('subscription.currentOrgType', 'Current')}
                        size="small"
                        color="success"
                        sx={{ ml: 1, fontSize: '0.7rem', height: 22 }}
                      />
                    )}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>
        )}

        <Grid container spacing={3} justifyContent="center" sx={{ overflow: 'visible' }}>
          {visiblePlans.map((plan) => {
            const isCurrent = currentPlanCode === plan.code;
            const isActionLoading = actionPriceId === plan.priceId;
            const isTypeChange =
              organizationType != null && plan.organizationType !== organizationType;
            const isUpgrade =
              mode === 'change' &&
              !isCurrent &&
              !isTypeChange &&
              tierRank[plan.tier] > tierRank[planCodeToTier(currentPlanCode)];
            const isEmphasized = !isCurrent && (isTypeChange || isUpgrade);
            const isPrivateOffer = isPrivateOfferPlanCode(plan.code);
            const isPopular = isPopularPlanCode(plan.code);
            const specialOfferIndex = isPrivateOffer
              ? specialOfferPlans.findIndex((offer) => offer.priceId === plan.priceId)
              : -1;
            const accent = isPrivateOffer
              ? resolveSpecialOfferAccent(
                  specialOfferIndex,
                  specialOfferPlans.length,
                  theme,
                )
              : resolvePlanAccent(plan.code, plan.tier, theme);
            const isHighlighted = !isCurrent && (isEmphasized || isPopular || isPrivateOffer);
            const canSelect = !isCurrent && !actionPriceId;

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={visiblePlans.length <= 2 ? 5 : 4}
                key={plan.priceId}
                sx={{
                  overflow: 'visible',
                  pt: isPopular || isPrivateOffer || isCurrent ? 1.5 : 0,
                }}
              >
                <SubscriptionPlanCard
                  plan={plan}
                  accent={accent}
                  isCurrent={isCurrent}
                  isPopular={isPopular}
                  isPrivateOffer={isPrivateOffer}
                  isHighlighted={isHighlighted}
                  onboarding={showTrialPricing}
                  canSelect={canSelect}
                  muted={isCurrent && (mode === 'change' || subscribedPreview)}
                  actionLabel={actionLabel(plan)}
                  actionLoading={isActionLoading}
                  actionDisabled={isCurrent || Boolean(actionPriceId)}
                  actionStartIcon={
                    !isCurrent &&
                    (mode === 'change' || subscribedPreview) &&
                    !isActionLoading ? (
                      isPrivateOffer || subscribedPreview ? (
                        <SwapHorizIcon />
                      ) : (
                        <OpenInNewIcon />
                      )
                    ) : undefined
                  }
                  onSelect={() => requestPlanAction(plan)}
                />
              </Grid>
            );
          })}
        </Grid>

        {showTrialPricing && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 3, maxWidth: 520, mx: 'auto' }}
          >
            {mode === 'preview'
              ? t(
                  'subscription.pricing.billingFootnote',
                  'Start with a free month after you create an account. Confirm payment details in Stripe — cancel before the trial ends to avoid billing.',
                )
              : t(
                  'subscription.onboarding.billingFootnote',
                  'You will confirm payment details in Stripe. No charge during the free month. Cancel before it ends to avoid billing.',
                )}
          </Typography>
        )}

        <Dialog
          open={Boolean(pendingTypeChangePlan)}
          onClose={() => setPendingTypeChangePlan(null)}
          maxWidth="xs"
          fullWidth
          aria-labelledby="org-type-change-warning-title"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pt: 2.5, px: { xs: 2.25, sm: 3 }, pb: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(
                    pendingStudioTransition
                      ? theme.palette.warning.main
                      : theme.palette.primary.main,
                    0.14,
                  ),
                  color: pendingStudioTransition
                    ? theme.palette.warning.main
                    : theme.palette.primary.main,
                  border: `1px solid ${alpha(
                    pendingStudioTransition
                      ? theme.palette.warning.main
                      : theme.palette.primary.main,
                    0.28,
                  )}`,
                  flexShrink: 0,
                }}
              >
                {pendingStudioTransition ? (
                  <WarningAmberRoundedIcon fontSize="small" />
                ) : (
                  <SwapHorizIcon fontSize="small" />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.35 }}>
                <Typography
                  id="org-type-change-warning-title"
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' }}
                >
                  {pendingTypeChangePlan
                    ? t('subscription.orgTypeChangeTitle', 'Switch to {{type}}?', {
                        type: orgTypeLabel(pendingTypeChangePlan.organizationType),
                      })
                    : null}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {pendingStudioTransition
                    ? t(
                        'subscription.studioWarningSubtitle',
                        'Tutor discovery will pause until you switch back.',
                      )
                    : pendingTypeChangePlan && isPrivateOfferPlanCode(pendingTypeChangePlan.code)
                      ? t(
                          'subscription.orgTypeChangeSubtitleOffer',
                          'Organization type updates after the special offer is applied.',
                        )
                      : t(
                          'subscription.orgTypeChangeSubtitle',
                          'Organization type updates after Stripe confirms.',
                        )}
                </Typography>
              </Box>
              <IconButton
                aria-label={t('common.close', 'Close')}
                onClick={() => setPendingTypeChangePlan(null)}
                edge="end"
                size="small"
                sx={{ mt: 0.15 }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2.25, sm: 3 }, pt: 1, pb: 1.5 }}>
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                {organizationType && pendingTypeChangePlan
                  ? isPrivateOfferPlanCode(pendingTypeChangePlan.code)
                    ? t(
                        'subscription.orgTypeChangeBodyOffer',
                        'The special offer applies without opening Stripe Billing. Features and navigation for {{from}} switch to {{to}} once the new plan is active.',
                        {
                          from: orgTypeLabel(organizationType),
                          to: orgTypeLabel(pendingTypeChangePlan.organizationType),
                        },
                      )
                    : t(
                        'subscription.orgTypeChangeBody',
                        'You will finish in Stripe. Features and navigation for {{from}} switch to {{to}} once the new plan is active.',
                        {
                          from: orgTypeLabel(organizationType),
                          to: orgTypeLabel(pendingTypeChangePlan.organizationType),
                        },
                      )
                  : null}
              </Typography>
              {pendingStudioTransition ? (
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                  {t(
                    'subscription.studioWarningBody',
                    'Your tutors will be removed from public search. Their profiles and bookings stay in the system, but they will not appear publicly again until you manually turn their visibility back on after switching to a school or tutor plan.',
                  )}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2.25, sm: 3 },
              pb: { xs: 2.25, sm: 3 },
              pt: 1,
              gap: 1,
            }}
          >
            <Button onClick={() => setPendingTypeChangePlan(null)} sx={{ fontWeight: 600 }}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color={pendingStudioTransition ? 'warning' : 'primary'}
              onClick={confirmTypeChange}
              autoFocus
              sx={{ fontWeight: 700 }}
            >
              {t('subscription.orgTypeChangeConfirm', 'Continue')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  },
);

export default SubscriptionPlans;
